import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Config } from '@generated/models';
import { CONFIG } from '@web/core/injection-tokens/config.token';
import { MultiSidebarComponent } from '@web/shared/components/multi-sidebar/multi-sidebar.component';
import { filter, map, startWith } from 'rxjs';
import { createMainSidebarNavItems } from './main-sidebar-nav.config';

const COLLAPSED_STORAGE_KEY = 'soe.mainSidebar.collapsed';

@Component({
  selector: 'app-main-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MultiSidebarComponent, DatePipe],
  template: `
    <div class="flex h-full items-start">
      <div class="flex flex-col h-full bg-soe-dark-gray rounded-xl overflow-hidden">
        <app-multi-sidebar
          class="flex-1 min-h-0"
          [items]="navItems()"
          [currentUrl]="currentUrl()"
          [collapsed]="collapsed()"
          [expandLabel]="expandLabel"
          [collapseLabel]="collapseLabel"
          (collapsedChange)="onCollapsedChange($event)"
        >
          <div
            multiSidebarFooter
            class="build-info flex flex-row flex-wrap items-center justify-center gap-x-2 gap-y-0 text-[0.65rem] leading-none text-white/70 whitespace-nowrap"
          >
            @let _version = buildVersion();
            <span>Web: {{ _version.web }}</span>
            <span>API: {{ _version.api }}</span>
            <span>{{ buildDate() | date: 'short' }}</span>
          </div>
        </app-multi-sidebar>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
    }
  `,
})
export class MainSidebarComponent {
  private readonly config = inject(CONFIG);
  private readonly router = inject(Router);

  readonly buildInfo = input.required<{
    api: Config['buildInfo'] | undefined;
    web: { version: string; date: string };
  }>();

  readonly expandLabel = $localize`Expand`;
  readonly collapseLabel = $localize`Collapse`;

  readonly collapsed = signal(readCollapsedPreference());

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly navItems = computed(() => createMainSidebarNavItems(this.config));

  readonly buildVersion = computed(() => {
    const info = this.buildInfo();
    return {
      api: info.api?.version ?? 'N/A',
      web: info.web.version,
    };
  });

  readonly buildDate = computed(() => {
    const info = this.buildInfo();
    if (!info.api?.date) {
      return info.web.date;
    }
    return info.api.date > info.web.date ? info.api.date : info.web.date;
  });

  onCollapsedChange(collapsed: boolean): void {
    this.collapsed.set(collapsed);
    writeCollapsedPreference(collapsed);
  }
}

function readCollapsedPreference(): boolean {
  try {
    const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (raw === null) {
      return false;
    }
    return raw === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // ignore
  }
}
